<script lang="ts" setup>
import { computed, inject, ref } from "vue";
import { TranslationService } from "../services/translation.service";
import { OverlaysService } from "../services/overlays.service";
import { CountryCodes } from "../CountryCodes";

import { EOverlay } from "../EOverlay";
import axios from "axios";
import { SITEURL } from "@libTs/cms.globals";
import { IAjaxResponse } from "../Ajax";
import { vDropdown as dropdownDirective } from "../directives/v-dropdown";
import { vInputText as vInputTextDir } from "../directives/v-input-text";

const vInputText = vInputTextDir;

const { titles } = defineProps({
    titles: {
        type: Array<string>,
        required: true,
    },
});

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;
const overlaysSvc = inject<OverlaysService>("overlaysSvc") as OverlaysService;
const countries = CountryCodes;
const vDropdown = dropdownDirective;

type sendingStatus =
    | "ok"
    | "error"
    | "set-new-pw-mail"
    | "use-reset-pw"
    | "invalid-email"
    | "rate-limited"
    | "validation-error"
    | number;
const sendingStatus = ref<sendingStatus | null>(null);
const validationErrors = ref<string[]>([]);

interface IRegisterAjaxResponse extends Pick<IAjaxResponse, "sent"> {
    status: sendingStatus;
    errors?: string[];
}

interface IRegisterFormData {
    title: string;
    firstname?: string;
    lastname: string;
    organization: string;
    department?: string;
    street?: string;
    postcode: string;
    city: string;
    country?: string;
    telephone?: string;
    fax?: string;
    email: string;
    // Honeypot field: must stay empty. Hidden from real users via CSS;
    // bots that auto-fill every input will populate it, letting the
    // backend silently drop the submission. Named to avoid matching any
    // password-manager autofill category (e.g. "website", "email").
    hpCheck2b: string;
}

const getCleanFormModelData = (): IRegisterFormData => ({
    title: "",
    firstname: "",
    lastname: "",
    organization: "",
    department: "",
    street: "",
    postcode: "",
    city: "",
    country: "",
    telephone: "",
    fax: "",
    email: "",
    hpCheck2b: "",
});

const registerData = ref<IRegisterFormData>(getCleanFormModelData());
const sendingSuccessful = ref(false);
const isSending = ref(false);

const formPristine = computed(() => {
    return Object.values(registerData.value).every(
        (element) => element.length === 0,
    );
});

const formInvalid = computed(() => {
    return (
        formPristine.value ||
        registerData.value.title.length === 0 ||
        registerData.value.lastname.length === 0 ||
        registerData.value.organization.length === 0 ||
        registerData.value.postcode.length === 0 ||
        registerData.value.city.length === 0 ||
        registerData.value.email.length === 0
    );
});

const sendForm = (): void => {
    if (
        formInvalid.value ||
        formPristine.value ||
        sendingSuccessful.value ||
        typeof registerData.value.title === "undefined" ||
        registerData.value.title.length <= 0
    ) {
        console.warn("Register form not valid.");
        return;
    }

    if (
        typeof registerData.value.country !== "string" ||
        registerData.value.country.length === 0
    ) {
        registerData.value.country = "CH";
    }

    sendingSuccessful.value = false;
    validationErrors.value = [];
    isSending.value = true;

    axios
        .post<IRegisterAjaxResponse>(
            SITEURL + "actions/sapShop/registration/new",
            registerData.value,
            {
                responseType: "json",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .then(
            // success (2xx)
            (response) => {
                const data = response.data;
                if (data && data.sent) {
                    sendingStatus.value = data.status;

                    if (data.status === "ok") {
                        sendingSuccessful.value = true;

                        // console.log("Register email successfully sent.");
                    } else if (data.status === "set-new-pw-mail") {
                        sendingSuccessful.value = true;
                        registerData.value = getCleanFormModelData();
                        // console.log(
                        //     "Email for new user with pw set instructions successfully sent.",
                        // );
                    } else {
                        sendingSuccessful.value = false;
                        console.warn(
                            'Error "' +
                                data.status +
                                '": The email has not been sent. ',
                        );
                    }
                } else {
                    sendingSuccessful.value = false;
                    sendingStatus.value = data.status;

                    console.warn(
                        'Error "' +
                            data.status +
                            '": The email has not been sent. ',
                    );
                }
            },

            // error (non-2xx, e.g. 400 validation error, 429 rate limit)
            (error) => {
                sendingSuccessful.value = false;

                const data: IRegisterAjaxResponse | undefined =
                    error?.response?.data;

                if (data && data.status) {
                    sendingStatus.value = data.status;
                    if (
                        data.status === "validation-error" &&
                        Array.isArray(data.errors)
                    ) {
                        validationErrors.value = data.errors;
                    }
                } else {
                    sendingStatus.value = "error";
                }

                console.warn("Error: The email has not been sent. ");
                // console.log(error);
            },
        )
        .finally(() => {
            isSending.value = false;
        });
};

const escapeHtml = (value: string): string => {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
};

const userAlreadyExistsMessage = computed<string>(() => {
    return t("USER_ALREADY_EXISTS").replace(
        "%email%",
        `<i>${escapeHtml(registerData.value.email)}</i>`,
    );
});

const errorMessage = computed<string | null>(() => {
    switch (sendingStatus.value) {
        case "invalid-email":
            return t("REGISTRATION_ERROR_INVALID_EMAIL");
        case "rate-limited":
            return t("REGISTRATION_ERROR_RATE_LIMITED");
        case "validation-error":
            return null; // shown via validationErrors list instead
        case null:
        case "ok":
        case "set-new-pw-mail":
        case "use-reset-pw":
            return null;
        default:
            return t("CONNECTION_ERROR");
    }
});

// Statuses that mean "something needs fixing, keep the form visible so the
// user can correct it and resubmit" (as opposed to "ok"/"use-reset-pw"/
// "set-new-pw-mail", which replace the form with a dedicated message).
const formStaysVisible = computed<boolean>(() => {
    return (
        !sendingStatus.value ||
        sendingStatus.value === "error" ||
        sendingStatus.value === "invalid-email" ||
        sendingStatus.value === "rate-limited" ||
        sendingStatus.value === "validation-error"
    );
});
</script>

<template>
    <form
        name="form"
        :style="formStaysVisible ? {} : { display: 'none' }"
        @submit.prevent="sendForm()"
        novalidate
    >
        <!-- Honeypot field: hidden from real users, only bots fill it in. -->
        <div
            aria-hidden="true"
            style="
                position: absolute;
                left: -9999px;
                width: 1px;
                height: 1px;
                overflow: hidden;
            "
        >
            <label for="hpCheck2b">Leave this field empty</label>
            <input
                id="hpCheck2b"
                type="text"
                name="hpCheck2b"
                tabindex="-1"
                autocomplete="off"
                v-model="registerData.hpCheck2b"
            />
        </div>
        <div class="mt-10">
            <div class="h2">{{ t("REGISTRATION") }}</div>
            <div class="mt-10" v-html="t('REGISTRATION_FORM_TEXT')"></div>
        </div>
        <div class="mt-10 mb-6">
            <label class="text-f7">Title *</label>
            <div
                class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                v-dropdown
            >
                <div class="input-text rounded-l-s">
                    <input
                        class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-l-s"
                        type="text"
                        autocomplete="off"
                        :placeholder="t('REGISTER_FIRST_TITLE')"
                        disabled="true"
                        v-model="registerData.title"
                    />
                </div>
                <div class="input-text-btn relative rounded-r-s bg-grey-50">
                    <i
                        class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                    ></i>
                </div>
                <div class="input-dropdown-elements bgcolor-white w-full">
                    <div class="input-dropdown-elements-inner">
                        <ul>
                            <li
                                v-for="item in titles"
                                class="text-small bold"
                                @click="registerData.title = item"
                            >
                                {{ item }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <div class="mb-6">
            <label class="text-f7">First name *</label>
            <input
                type="text"
                name="fname"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.firstname"
                :placeholder="t('REGISTER_FIRST_NAME')"
            />
        </div>
        <div class="mb-6">
            <label class="text-f7">Last name *</label>
            <input
                type="text"
                name="lname"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.lastname"
                :placeholder="t('REGISTER_LAST_NAME')"
                required
            />
        </div>
        <div class="mb-6">
            <label class="text-f7">Company *</label>
            <input
                type="text"
                name="organization"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.organization"
                :placeholder="t('REGISTER_COMPANY')"
                required
            />
        </div>
        <div class="mb-6">
            <label class="text-f7">Department *</label>
            <input
                type="text"
                name="department"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.department"
                :placeholder="t('REGISTER_DEPARTMENT')"
            />
        </div>
        <div class="mb-6">
            <label class="text-f7">Street *</label>
            <input
                type="text"
                name="address"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.street"
                :placeholder="t('REGISTER_STREET')"
            />
        </div>
        <div class="mb-6 grid grid-cols-3 gap-4">
            <div class="col-span-1">
                <label class="text-f7">Postcode *</label>
                <input
                    type="text"
                    name="zip"
                    class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                    v-model="registerData.postcode"
                    :placeholder="t('REGISTER_POSTCODE')"
                    required
                />
            </div>
            <div class="col-span-2">
                <label class="text-f7">City *</label>
                <input
                    type="text"
                    name="city"
                    class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                    v-model="registerData.city"
                    :placeholder="t('REGISTER_CITY')"
                    required
                />
            </div>
        </div>
        <div class="mb-6">
            <label class="text-f7">Country *</label>
            <div
                class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                v-dropdown
            >
                <div class="input-text rounded-l-s">
                    <input
                        class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-l-s"
                        type="text"
                        autocomplete="off"
                        disabled="true"
                        :value="
                            countries[
                                (registerData.country as keyof typeof countries) ??
                                    'CH'
                            ] || 'Schweiz'
                        "
                    />
                </div>
                <div class="input-text-btn relative rounded-r-s bg-grey-50">
                    <i
                        class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                    ></i>
                </div>
                <div class="input-dropdown-elements bgcolor-white w-full">
                    <div class="input-dropdown-elements-inner">
                        <ul>
                            <li
                                class="text-small bold"
                                v-for="(cname, ccode) in countries"
                                @click="registerData.country = ccode"
                            >
                                {{ cname }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="mb-6">
            <label class="text-f7">Phone *</label>
            <input
                type="text"
                name="phone"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.telephone"
                :placeholder="t('REGISTER_TELEPHONE')"
            />
        </div>
        <div class="mb-6">
            <label class="text-f7">Fax *</label>
            <input
                type="text"
                name="fax"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.fax"
                :placeholder="t('REGISTER_FAX')"
            />
        </div>

        <div class="mb-6">
            <label class="text-f7">E-Mail *</label>
            <input
                type="email"
                name="email"
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                v-model="registerData.email"
                placeholder="E-Mail"
                required
            />
        </div>

        <div
            class="mt-5"
            :class="{ 'loading-animation': isSending }"
            style="display: inline-block"
        >
            <input
                type="submit"
                class="btn-cb"
                :disabled="
                    formPristine ||
                    formInvalid ||
                    sendingSuccessful ||
                    isSending
                "
                value="Register now"
            />
        </div>

        <div
            class="mt-10"
            :style="
                errorMessage || validationErrors.length
                    ? {}
                    : { display: 'none' }
            "
        >
            <div v-if="errorMessage">{{ errorMessage }}</div>
            <div v-if="validationErrors.length">
                <div>{{ t("REGISTRATION_ERROR_VALIDATION") }}</div>
                <ul>
                    <li v-for="err in validationErrors" :key="err">
                        {{ err }}
                    </li>
                </ul>
            </div>
        </div>
    </form>

    <!-- message: user already exists, with password (existing users)-->
    <div :style="sendingStatus == 'use-reset-pw' ? {} : { display: 'none' }">
        <div class="mt-10">
            <br />
            <div class="h2" v-html="userAlreadyExistsMessage"></div>
            <br />
            <p>
                <a
                    @click="
                        sendingSuccessful = false;
                        sendingStatus = null;
                    "
                >
                    {{ t("TRY_DIFFERENT_EMAIL") }}
                </a>
                {{ t("OR") }}
                <a @click="overlaysSvc.showOverlay(EOverlay.passwordForgotten)">
                    {{ t("RESET_PASSWORD") }}
                </a>
            </p>
        </div>
    </div>
    <!-- message: user already exists, without password (new users) -->
    <div :style="sendingStatus == 'set-new-pw-mail' ? {} : { display: 'none' }">
        <div class="mt-10">
            <br />
            <div class="h2">
                {{ t("REGISTER_THANKYOU") }}
            </div>
        </div>
    </div>

    <!-- sending successful message -->
    <div
        :style="
            sendingSuccessful && sendingStatus == 'ok'
                ? {}
                : { display: 'none' }
        "
    >
        <div class="mt-10">
            <br />
            <div class="h2">{{ t("REGISTRATION_REQUEST_SENT") }}</div>
            <br />
            <p>{{ t("REGISTRATION_REQUEST_FOLLOWUP") }}</p>
            <br />
            <a class="btn-cb" href="{{ siteUrl }}">{{ t("BACK_TO_HOME") }}</a>
        </div>
    </div>
</template>
